import React from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "../lib/leafletIcon";

import { SUMUT_CENTER, SUMUT_BOUNDS } from "../lib/wilayah";

const STATUS_COLOR = {
  "PO Diproses": "#8B5CF6",
  "Dalam Pengiriman": "#E67E22",
};

// Supplier marker color by alert_level (ticket-based)
const SUPPLIER_COLOR = {
  danger:  "#DC2626",  // red — has breached tickets
  warning: "#F59E0B",  // amber — has active tickets
  ok:      "#045498",  // brand blue — no active tickets
};

// Lokasi SPPG (dari Profil SPPG) — hijau agar tidak tertukar dengan supplier/pengiriman.
const SPPG_COLOR = { fill: "#10B981", stroke: "#047857" };

/** Legenda penanda peta, dipakai bersama oleh Dashboard dan Peta Distribusi. */
export function MapLegend({ className = "", tampil = "semua" }) {
  const item = (warna, label, bulatBerbingkai = false) => (
    <span className="flex items-center gap-1.5">
      <span
        className="w-2.5 h-2.5 rounded-full"
        style={bulatBerbingkai
          ? { backgroundColor: SPPG_COLOR.fill, boxShadow: `0 0 0 1.5px ${SPPG_COLOR.stroke}` }
          : { backgroundColor: warna }}
      />
      {label}
    </span>
  );
  return (
    <div className={`flex items-center gap-3 text-xs text-slate-500 flex-wrap ${className}`} data-testid="map-legend">
      {tampil !== "supplier" && item(null, "SPPG", true)}
      {tampil !== "sppg" && (
        <>
          {item(SUPPLIER_COLOR.ok, "Supplier (no tiket)")}
          {item(SUPPLIER_COLOR.warning, "Supplier · tiket aktif")}
          {item(SUPPLIER_COLOR.danger, "Supplier · SLA breach")}
        </>
      )}
      {tampil === "semua" && (
        <>
          {item(STATUS_COLOR["PO Diproses"], "PO Diproses")}
          {item(STATUS_COLOR["Dalam Pengiriman"], "Dalam Pengiriman")}
        </>
      )}
    </div>
  );
}

export default function MapView({ points = [], height = 480 }) {
  const [tileError, setTileError] = React.useState(false);
  const heightStyle = typeof height === "number" ? `${height}px` : height;
  return (
    <div className="relative rounded-lg overflow-hidden border border-slate-200" style={{ height: heightStyle }} data-testid="map-view">
      {tileError && (
        <div className="absolute inset-0 z-[1000] bg-slate-100/90 backdrop-blur-sm flex flex-col items-center justify-center p-4 text-center">
          <div className="text-red-600 font-medium bg-white p-4 rounded-lg shadow-md border border-red-200">
            <div className="text-xl mb-1">⚠</div>
            Peta gagal dimuat pastikan terhubung internet
          </div>
        </div>
      )}
      <MapContainer
        center={SUMUT_CENTER}
        zoom={8}
        minZoom={7}
        maxZoom={14}
        maxBounds={SUMUT_BOUNDS}
        maxBoundsViscosity={1.0}
        style={{ height: "100%", width: "100%" }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          eventHandlers={{
            tileerror: () => setTileError(true)
          }}
        />
        {points.map((p) => {
          if (p.type === "supplier") {
            const alert = p.alert_level || "ok";
            const color = SUPPLIER_COLOR[alert];
            const tickets = p.tickets || { open: 0, on_process: 0, closed: 0, breached: 0, total: 0 };
            const hasActive = (tickets.open + tickets.on_process) > 0;
            const radius = alert === "danger" ? 12 : alert === "warning" ? 10 : 9;
            return (
              <CircleMarker
                key={`s-${p.id}`}
                center={[p.lat, p.lng]}
                radius={radius}
                pathOptions={{
                  color,
                  fillColor: color,
                  fillOpacity: p.verified ? 0.85 : 0.5,
                  weight: alert === "danger" ? 3 : 2,
                }}
              >
                {/* Hover: nama usaha + jenis produk */}
                <Tooltip direction="top" offset={[0, -radius - 2]} opacity={1}>
                  <span style={{ fontSize: "11px" }}>
                    <strong>{p.name}</strong>
                    {(p.jenis_produk || []).length > 0 && ` · ${p.jenis_produk.join(", ")}`}
                  </span>
                </Tooltip>

                {/* Permanent count badge for suppliers with active tickets */}
                {hasActive && (
                  <Tooltip
                    direction="top"
                    permanent
                    offset={[0, -radius - 2]}
                    className="map-ticket-badge"
                  >
                    <span style={{
                      backgroundColor: color,
                      color: "white",
                      padding: "1px 6px",
                      borderRadius: "9999px",
                      fontSize: "10px",
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                    }}>
                      {tickets.open + tickets.on_process} tiket{tickets.breached > 0 ? ` · ${tickets.breached} breach` : ""}
                    </span>
                  </Tooltip>
                )}
                <Popup>
                  <div className="text-sm min-w-[200px]">
                    <div className="font-semibold text-slate-900">{p.name}</div>
                    <div className="text-slate-500 text-xs mt-0.5">Supplier · {p.kota}</div>
                    {(p.jenis_produk || []).length > 0 && (
                      <div className="text-xs text-slate-600 mt-1">
                        <span className="text-slate-400">Jenis produk: </span>
                        {p.jenis_produk.join(", ")}
                      </div>
                    )}
                    <div className="mt-1.5 flex items-center gap-1 flex-wrap">
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "1px 6px",
                        borderRadius: "9999px",
                        fontSize: "10px",
                        fontWeight: 500,
                        backgroundColor: p.verified ? "#D1FAE5" : "#F1F5F9",
                        color: p.verified ? "#065F46" : "#475569",
                        border: `1px solid ${p.verified ? "#A7F3D0" : "#CBD5E1"}`,
                      }}>
                        {p.verified ? "Terverifikasi" : "Belum Verifikasi"}
                      </span>
                    </div>

                    {/* Tickets section */}
                    {tickets.total > 0 ? (
                      <div className="mt-3 pt-2.5 border-t border-slate-200">
                        <div style={{
                          fontSize: "10px", fontWeight: 600, color: "#64748B",
                          textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4,
                        }}>
                          Tiket Kendala ({tickets.total})
                        </div>
                        <div className="grid grid-cols-3 gap-1 mt-1.5">
                          <BadgeCount label="Open" count={tickets.open} color="#1E40AF" bg="#DBEAFE" border="#BFDBFE" />
                          <BadgeCount label="On Proc" count={tickets.on_process} color="#92400E" bg="#FEF3C7" border="#FDE68A" />
                          <BadgeCount label="Closed" count={tickets.closed} color="#065F46" bg="#D1FAE5" border="#A7F3D0" />
                        </div>
                        {tickets.breached > 0 && (
                          <div style={{
                            marginTop: 6, padding: "4px 8px", backgroundColor: "#FEE2E2",
                            border: "1px solid #FECACA", borderRadius: 4,
                            color: "#991B1B", fontSize: 11, fontWeight: 600,
                          }}>
                            ⚠ {tickets.breached} tiket telah melewati SLA
                          </div>
                        )}
                        {tickets.near_breach > 0 && tickets.breached === 0 && (
                          <div style={{
                            marginTop: 6, padding: "4px 8px", backgroundColor: "#FEF3C7",
                            border: "1px solid #FDE68A", borderRadius: 4,
                            color: "#92400E", fontSize: 11, fontWeight: 600,
                          }}>
                            {tickets.near_breach} tiket mendekati SLA breach
                          </div>
                        )}
                        {tickets.latest_id && (
                          <Link
                            to={`/tickets/${tickets.latest_id}`}
                            style={{ color: "#045498", fontSize: 11, fontWeight: 600, textDecoration: "underline", marginTop: 6, display: "inline-block" }}
                          >
                            Lihat tiket terkini →
                          </Link>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-slate-400 italic">Tidak ada tiket terkait.</div>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            );
          }
          if (p.type === "sppg") {
            return (
              <CircleMarker
                key={`g-${p.id}`}
                center={[p.lat, p.lng]}
                radius={10}
                pathOptions={{
                  color: SPPG_COLOR.stroke,
                  fillColor: SPPG_COLOR.fill,
                  fillOpacity: 0.85,
                  weight: 3,
                }}
              >
                <Tooltip direction="top" offset={[0, -12]} opacity={1}>
                  <span style={{ fontSize: "11px" }}>
                    <strong>{p.name}</strong>
                    {p.jenis && ` · SPPG ${p.jenis}`}
                  </span>
                </Tooltip>
                <Popup>
                  <div className="text-sm min-w-[220px]">
                    <div className="font-semibold text-slate-900">{p.name}</div>
                    <div className="text-slate-500 text-xs mt-0.5">
                      SPPG{p.jenis ? ` · ${p.jenis}` : ""} · {p.kota}
                    </div>
                    {p.id_sppg && (
                      <div className="text-xs mt-1">
                        <span className="text-slate-400">ID SPPG: </span>
                        <span style={{ fontFamily: "monospace" }}>{p.id_sppg}</span>
                      </div>
                    )}
                    {p.alamat && (
                      <div className="text-xs text-slate-600 mt-1">
                        <span className="text-slate-400">Alamat: </span>{p.alamat}
                      </div>
                    )}
                    {p.pic && (
                      <div className="text-xs mt-1">
                        <span className="text-slate-400">Kepala SPPG: </span>{p.pic}
                      </div>
                    )}
                    {p.telp && (
                      <div className="text-xs mt-1">
                        <span className="text-slate-400">Telepon: </span>{p.telp}
                      </div>
                    )}
                    <div className="mt-2" style={{
                      display: "inline-block", padding: "2px 8px", borderRadius: 9999,
                      backgroundColor: p.po_aktif ? "#ECFDF5" : "#F1F5F9",
                      color: p.po_aktif ? "#047857" : "#64748B",
                      border: `1px solid ${p.po_aktif ? "#A7F3D0" : "#E2E8F0"}`,
                      fontSize: 11, fontWeight: 600,
                    }}>
                      {p.po_aktif ? `${p.po_aktif} pesanan berjalan` : "Tidak ada pesanan berjalan"}
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          }
          // delivery point — tujuan pengiriman selalu berada di lokasi SPPG, jadi
          // digambar lebih kecil: tampil sebagai titik di tengah cincin hijau SPPG
          // dan keduanya tetap bisa diklik.
          const color = STATUS_COLOR[p.status] || "#0EA5E9";
          return (
            <CircleMarker
              key={`d-${p.id}`}
              center={[p.lat, p.lng]}
              radius={5}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.7, weight: 2 }}
            >
              <Popup>
                <div className="text-sm">
                  <div className="font-semibold text-slate-900">{p.name}</div>
                  <div className="text-slate-500 text-xs mt-0.5">Pengiriman · {p.kota}</div>
                  <div className="text-xs mt-1">Supplier: {p.supplier_nama}</div>
                  {(p.supplier_jenis_produk || []).length > 0 && (
                    <div className="text-xs text-slate-500">Jenis produk: {p.supplier_jenis_produk.join(", ")}</div>
                  )}
                  <div className="text-xs mt-1">Status: <strong>{p.status}</strong></div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}

function BadgeCount({ label, count, color, bg, border }) {
  return (
    <div style={{
      backgroundColor: bg, border: `1px solid ${border}`,
      borderRadius: 4, padding: "3px 4px", textAlign: "center",
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color, lineHeight: 1 }}>{count}</div>
      <div style={{ fontSize: 9, color, opacity: 0.8, marginTop: 2, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
    </div>
  );
}
